<template>
	<div>
		<line-chart
			:datasets="datasets"
			:labels="labels"
			:options="options"
			:height="150"
			:width="300"></line-chart>
	</div>
</template>
<script>
	import { ref, computed, inject, onMounted, onBeforeUnmount, watch } from "vue";
	import { lineChart, lineChartHelper } from "@/components/charts";
	import numberFormat from "@/services/utils/numberFormat";
	import { kpi_datasets, CreateFingerprint } from "./kpiOutput.js";

	export default {
		components: {
			lineChart
		},
		props: {
			report: {
				type: Object,
				default: () => {
					return {
						results: [],
						compare: []
					};
				}
			},
			breakdown: {
				type: Object,
				default: () => {
					return undefined;
				}
			},
			colors: {
				type: Array,
				default: () => {
					return ["#00aeef", "#d9017a"];
				}
			},
			metric: {
				type: String,
				default: () => {
					return "totalBillings";
				}
			},
			config: {
				type: Object,
				default: () => {
					return {};
				}
			}
		},
		setup(props, context) {
			const moment = inject("$moment");
			const $financial = inject("$financial");

			const options = ref({
				responsive: true,
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						callbacks: {
							title: (tooltipItems) => {
								const tooltip = tooltipItems[0];
								const labelType = tooltip.dataset.label;
								if (labelType == "Compare") {
									const label = moment(tooltip.label, "[Q]Q YY");
									label.add(-1, "year");
									return label.format("[Q]Q YY");
								} else return tooltip.label;
							},
							label: (tooltipItem) => {
								return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
									tooltipItem.raw
								);
							}
						}
					}
				},
				scales: {
					y: {
						suggestedMin: 0,
						ticks: {
							callback: function (value, index, ticks) {
								return numberFormat.compact(value, 0);
							},
							maxTicksLimit: 4
						}
					},
					x: {
						grid: { display: false }
					}
				}
			});

			const results = computed(() => {
				if (props.breakdown) {
					return props.breakdown.api.BreakdownFilterResults(props.report.results);
				}
				return props.report.results;
			});

			const compare = computed(() => {
				if (props.breakdown) {
					return props.breakdown.api.BreakdownFilterResults(props.report.compare);
				}
				return props.report.compare;
			});

			const datasets = ref([]);

			const ToQuarter = (d) => {
				return "Q" + moment(d).format("Q YY");
			};

			const hasReportData = computed(() => {
				return (
					props.report.results &&
					props.report.results.length > 0 &&
					props.report.compare &&
					props.report.compare.length > 0
				);
			});

			const dateField = computed(() => {
				switch (props.metric) {
					case "totalBillings":
					case "teamingValue":
					case "teamingRevenue":
					case "executive":
					case "executiveSearch":
					case "consulting":
					case "im":
					case "strategyBillings":
					case "strategy":
					case "headcount":
						return "invoiceQuarter";
					case "filledJobValue":
					case "filled":
					case "timeToFill":
						return "statusQuarter";
					case "strategyTaken":
					case "taken":
						return "takenQuarter";
					default:
						return "createdQuarter";
				}
			});

			const isLastQuarter = computed(() => {
				const currentQuarter = ToQuarter(moment.utc().toDate());
				const lastLabel = labels.value[labels.value.length - 1];
				if (lastLabel === currentQuarter) {
					return true;
				} else {
					return false;
				}
			});

			const labels = computed(() => {
				if (
					!(
						props.report &&
						props.report.filter &&
						props.report.filter.date &&
						props.report.filter.date.endDate
					)
				) {
					return [];
				}

				const labels = [];
				const endDate = moment(props.report.filter.date.endDate);

				while (endDate > moment(props.report.filter.date.startDate)) {
					labels.push(ToQuarter(endDate));

					endDate.add(-1, "Q");
				}

				return labels.reverse();
			});

			const compareLabels = computed(() => {
				if (
					!(
						props.report &&
						props.report.filter &&
						props.report.filter.date &&
						props.report.filter.date.endDate
					)
				) {
					return [];
				}

				const labels = [];
				const endDate = moment(props.report.filter.date.endDate).add(-1, "year");
				const startDate = moment(props.report.filter.date.startDate).add(-1, "year");

				while (endDate > startDate) {
					labels.push(ToQuarter(endDate));

					endDate.add(-1, "Q");
				}

				return labels.reverse();
			});

			const BuildDatasets = () => {
				datasets.value = [];

				if (results.value.length > 0) {
					const set = {
						label: "Current",
						data: [],
						fill: false,
						borderColor: props.colors[0],
						tension: 0.1,
						spanGaps: true
					};
					if (isLastQuarter.value === true) {
						set.segment = {
							borderColor: (ctx) => lineChartHelper.last(ctx, labels.value.length, "rgb(0,0,0,0.2)"),
							borderDash: (ctx) => lineChartHelper.last(ctx, labels.value.length, [6, 6])
						};
					}
					if (props.config.includeSegment === false) {
						set.segment = undefined;
					}

					const groups = results.value.reduce((acc, row) => {
						const q = row[dateField.value]; // ToQuarter(moment(row[dateField.value]));

						if (!labels.value.find((x) => x == q)) return acc;

						if (!acc[q]) {
							acc[q] = [];
						}

						acc[q].push(row);
						return acc;
					}, []);

					labels.value.forEach((label, i, self) => {
						set.data.push(ProcessMetric(groups[label] || [], false));
					});
					set.pointRadius = set.data.map((d, i, self) => {
						if (i === self.length - 1) {
							return 3;
						} else {
							return 3;
						}
					});
					datasets.value.push(set);
				}

				if (compare.value.length > 0) {
					const set = {
						label: "Compare",
						data: [],
						fill: false,
						borderColor: props.colors[1],
						tension: 0.1,
						spanGaps: true
					};
					if (isLastQuarter.value === true) {
						set.segment = {
							borderColor: (ctx) => lineChartHelper.last(ctx, labels.value.length, "rgb(0,0,0,0.2)"),
							borderDash: (ctx) => lineChartHelper.last(ctx, labels.value.length, [6, 6])
						};
					}
					if (props.config.includeSegment === false) {
						set.segment = undefined;
					}

					const groups = compare.value.reduce((acc, row) => {
						const q = row[dateField.value];

						if (!compareLabels.value.find((x) => x == q)) return acc;

						if (!acc[q]) {
							acc[q] = [];
						}

						acc[q].push(row);
						return acc;
					}, []);

					compareLabels.value.forEach((label, i, self) => {
						set.data.push(ProcessMetric(groups[label] || [], true));
					});
					set.pointRadius = set.data.map((d, i, self) => {
						if (i === self.length - 1) {
							return 3;
						} else {
							return 3;
						}
					});
					datasets.value.push(set);
				}

				kpi_datasets.value[props.metric] = {
					metric: props.metric,
					labels: labels.value,
					datasets: datasets.value
				};
			};

			const ProcessMetric = (rows, isCompare) => {
				switch (props.metric) {
					case "assignmentsTaken": {
						return !isCompare
							? $financial.getAssignmentsTaken(rows)
							: $financial.compare.getAssignmentsTaken(rows);
					}
					case "totalBillings": {
						return !isCompare
							? $financial.getTotalBillings(rows)
							: $financial.compare.getTotalBillings(rows);
					}
					case "teamingValue": {
						return !isCompare
							? $financial.getTeamingValue(rows)
							: $financial.compare.getTeamingValue(rows);
					}
					case "teamingRevenue": {
						return !isCompare
							? $financial.getTeamingRevenue(rows)
							: $financial.compare.getTeamingRevenue(rows);
					}
					case "teamingTaken": {
						return !isCompare
							? $financial.getTeamingTaken(rows)
							: $financial.compare.getTeamingTaken(rows);
					}
					case "timeToFill": {
						return !isCompare
							? $financial.getTimeToFill(rows)
							: $financial.compare.getTeamingTaken(rows);
					}
					case "executive":
					case "executiveSearch": {
						return !isCompare ? $financial.getExecutive(rows) : $financial.compare.getExecutive(rows);
					}
					case "consulting": {
						return !isCompare
							? $financial.getConsulting(rows)
							: $financial.compare.getConsulting(rows);
					}
					case "im": {
						return !isCompare ? $financial.getIM(rows) : $financial.compare.getIM(rows);
					}
					case "strategyBillings":
					case "strategy": {
						return !isCompare
							? $financial.getStrategyBillings(rows)
							: $financial.compare.getStrategyBillings(rows);
					}
					case "filledJobValue": {
						return !isCompare
							? $financial.getFilledJobValue(rows)
							: $financial.compare.getFilledJobValue(rows);
					}
					case "strategyTaken": {
						return !isCompare
							? $financial.getStrategyTaken(rows)
							: $financial.compare.getStrategyTaken(rows);
					}
					case "taken": {
						return !isCompare
							? $financial.getAssignmentsTaken(rows)
							: $financial.compare.getAssignmentsTaken(rows);
					}
					case "filled": {
						return !isCompare
							? $financial.getAssignmentsFilled(rows)
							: $financial.compare.getAssignmentsFilled(rows);
					}
					case "headcount": {
						return !isCompare ? $financial.getHeadcount(rows) : $financial.compare.getHeadcount(rows);
					}
					default: {
						return rows.reduce((acc, v) => acc + v["totalBillings"], 0) ?? 0;
					}
				}
			};

			watch(
				() => props.report.filter,
				() => {
					BuildDatasets();
				},
				{ deep: true, immediate: true }
			);

			watch(
				() => results.value,
				() => {
					BuildDatasets();
				}
			);

			return {
				labels,
				datasets,
				options
			};
		}
	};
</script>
